import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useToggleSet } from '../hooks';
import {
  Zone,
  Section,
  Element,
  SubElement,
  TaskStatus,
  TaskStatusLabels,
  TaskStatusColors,
  PriorityLabels,
  PriorityColors,
  EditingState,
  AddingState,
} from '../types';
import {
  calculateZoneProgress,
  calculateSectionProgress,
  calculateElementProgress,
  calculateActualHours,
  getUserName,
} from '../data/mockData';
import { formatHours, formatPercentage, formatDateShort, getEffectivePriority, isAutoEscalated } from '../utils/format';
import { EditIcon, DeleteIcon, SaveIcon, CancelIcon, PlusIcon, PaperclipIcon, ChatIcon, FolderIcon, CopyIcon, WorkspaceIcon, UsersIcon } from '../components/icons/Icons';
import { useToast } from '../contexts/ToastContext';
import { FEATURES } from '../config/features';
import { Avatar } from '../components/common/Avatar';
import { AppEmptyState, AppSectionHero, AppStatCard, AppToolbar } from '../components/common/AppSurface';
import { useQuickTask } from '../hooks/useQuickTask';
import './WorkspacePage.css';

/* ==================== Component ==================== */
export const WorkspacePage: React.FC = () => {
  const { isProjectManager } = useAuth();
  const {
    projects,
    activeProject,
    activeProjectMembers,
    allUsers,
    addZone, updateZone, deleteZone,
    addSection, updateSection, deleteSection,
    addElement, updateElement, deleteElement,
    addSubElement, updateSubElementFields, deleteSubElement,
    duplicateZone, duplicateSection, duplicateElement, duplicateSubElement,
  } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const usersById = useMemo(() => new Map(allUsers.map(user => [user.id, user])), [allUsers]);
  const [highlightNodeId, setHighlightNodeId] = useState<string | null>(null);

  // Read saved expand state from sessionStorage to use as initial values
  const savedExpand = useMemo(() => {
    const pid = activeProject?.id;
    if (!pid) return { zones: [] as string[], sections: [] as string[], elements: [] as string[] };
    try {
      const raw = sessionStorage.getItem(`ws_expand_${pid}`);
      if (raw) {
        const data = JSON.parse(raw);
        return {
          zones: Array.isArray(data.zones) ? data.zones as string[] : [],
          sections: Array.isArray(data.sections) ? data.sections as string[] : [],
          elements: Array.isArray(data.elements) ? data.elements as string[] : [],
        };
      }
    } catch { /* ignore */ }
    return { zones: [] as string[], sections: [] as string[], elements: [] as string[] };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { set: expandedZones, toggle: toggleZone, addAll: addZones, replace: replaceZones, clear: clearZones } = useToggleSet(savedExpand.zones);
  const { set: expandedSections, toggle: toggleSection, addAll: addSections, replace: replaceSections, clear: clearSections } = useToggleSet(savedExpand.sections);
  const { set: expandedElements, toggle: toggleElement, addAll: addElements, replace: replaceElements, clear: clearElements } = useToggleSet(savedExpand.elements);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'ALL'>('ALL');

  /* ========== Focus a node coming from Global Search ========== */
  // location.state.focusNode = { kind: 'zone'|'section'|'element', id }
  // Expand the ancestor chain, then scroll to and highlight the row.
  useEffect(() => {
    const focus = (location.state as { focusNode?: { kind: string; id: string } } | null)?.focusNode;
    if (!focus || !activeProject) return;

    const zonesToOpen: string[] = [];
    const sectionsToOpen: string[] = [];
    const elementsToOpen: string[] = [];

    outer:
    for (const zone of activeProject.zones) {
      if (zone.id === focus.id) { zonesToOpen.push(zone.id); break; }
      for (const section of zone.sections) {
        if (section.id === focus.id) { zonesToOpen.push(zone.id); sectionsToOpen.push(section.id); break outer; }
        for (const element of section.elements) {
          if (element.id === focus.id) {
            zonesToOpen.push(zone.id); sectionsToOpen.push(section.id); elementsToOpen.push(element.id);
            break outer;
          }
        }
      }
    }

    if (zonesToOpen.length) addZones(zonesToOpen);
    if (sectionsToOpen.length) addSections(sectionsToOpen);
    if (elementsToOpen.length) addElements(elementsToOpen);

    setHighlightNodeId(focus.id);
    // Let the expanded rows render before scrolling.
    const timer = window.setTimeout(() => {
      document.querySelector(`[data-node-id="${focus.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    const clearHl = window.setTimeout(() => setHighlightNodeId(null), 2600);

    // Clear the state so re-renders / back-navigation don't re-trigger the focus.
    navigate('.', { replace: true, state: {} });

    return () => { window.clearTimeout(timer); window.clearTimeout(clearHl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, activeProject?.id]);

  // CRUD states
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [adding, setAdding] = useState<AddingState | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Quick Task modal (Option A: create a task without building the tree first)
  const quickTask = useQuickTask();

  /* ========== Persist Expand State ========== */
  // Skip saving on  first render (already initialized from sessionStorage)
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!activeProject) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      sessionStorage.setItem(`ws_expand_${activeProject.id}`, JSON.stringify({
        zones: Array.from(expandedZones),
        sections: Array.from(expandedSections),
        elements: Array.from(expandedElements),
      }));
    } catch { /* ignore */ }
  }, [expandedZones, expandedSections, expandedElements, activeProject]);

  /* ========== Expand / Collapse ========== */
  const expandAll = () => {
    if (!activeProject) return;
    replaceZones(activeProject.zones.map(z => z.id));
    replaceSections(activeProject.zones.flatMap(z => z.sections.map(s => s.id)));
    replaceElements(activeProject.zones.flatMap(z => z.sections.flatMap(s => s.elements.map(e => e.id))));
  };
  const collapseAll = () => { clearZones(); clearSections(); clearElements(); };

  /* ========== CRUD Handlers ========== */
  const startEditing = (id: string, code: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation(); setEditing({ id, code, name }); setAdding(null); setConfirmDeleteId(null);
  };
  const cancelEditing = (e: React.MouseEvent) => { e.stopPropagation(); setEditing(null); };

  const LEVEL_LABELS = { zone: 'Zone', section: 'Section', element: 'Element', subElement: 'Task' } as const;

  const hasDuplicateCode = (
    type: 'zone' | 'section' | 'element' | 'subElement',
    code: string,
    parentId?: string,
    currentId?: string,
  ): boolean => {
    if (!activeProject) return false;
    const normalized = code.trim().toLowerCase();
    if (type === 'zone') {
      return activeProject.zones.some(zone => zone.id !== currentId && zone.code.trim().toLowerCase() === normalized);
    }
    if (type === 'section') {
      const zone = activeProject.zones.find(zone => zone.id === parentId || zone.sections.some(section => section.id === currentId));
      return !!zone?.sections.some(section => section.id !== currentId && section.code.trim().toLowerCase() === normalized);
    }
    if (type === 'element') {
      const section = activeProject.zones
        .flatMap(zone => zone.sections)
        .find(section => section.id === parentId || section.elements.some(element => element.id === currentId));
      return !!section?.elements.some(element => element.id !== currentId && element.code.trim().toLowerCase() === normalized);
    }
    const element = activeProject.zones
      .flatMap(zone => zone.sections)
      .flatMap(section => section.elements)
      .find(element => element.id === parentId || element.subElements.some(task => task.id === currentId));
    return !!element?.subElements.some(task => task.id !== currentId && task.code.trim().toLowerCase() === normalized);
  };

  const saveEditing = (type: 'zone' | 'section' | 'element' | 'subElement', e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editing || !editing.code.trim() || !editing.name.trim()) return;
    const c = editing.code.trim(), n = editing.name.trim();
    if (hasDuplicateCode(type, c, undefined, editing.id)) {
      showToast(`${LEVEL_LABELS[type]} code already exists at this level`, 'error');
      return;
    }
    if (type === 'zone') updateZone(editing.id, { code: c, name: n });
    else if (type === 'section') updateSection(editing.id, { code: c, name: n });
    else if (type === 'element') updateElement(editing.id, { code: c, name: n });
    else updateSubElementFields(editing.id, { code: c, name: n });
    showToast(`${LEVEL_LABELS[type]} updated successfully`, 'success');
    setEditing(null);
  };

  const startAdding = (parentId: string, type: AddingState['type'], e: React.MouseEvent) => {
    e.stopPropagation(); setAdding({ parentId, type, code: '', name: '' }); setEditing(null); setConfirmDeleteId(null);
  };
  const cancelAdding = (e: React.MouseEvent) => { e.stopPropagation(); setAdding(null); };

  const saveAdding = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!adding || !adding.code.trim() || !adding.name.trim()) return;
    const c = adding.code.trim(), n = adding.name.trim();
    if (hasDuplicateCode(adding.type, c, adding.parentId)) {
      showToast(`${LEVEL_LABELS[adding.type]} code already exists at this level`, 'error');
      return;
    }
    if (adding.type === 'zone') addZone(adding.parentId, { code: c, name: n, description: '', projectId: adding.parentId });
    else if (adding.type === 'section') addSection(adding.parentId, { code: c, name: n, description: '', zoneId: adding.parentId });
    else if (adding.type === 'element') addElement(adding.parentId, { code: c, name: n, description: '', sectionId: adding.parentId });
    else addSubElement(adding.parentId, { code: c, name: n, description: '', elementId: adding.parentId, status: 'ASSIGNED', priority: 'MEDIUM', estimatedHours: 0, assignedTo: [], startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0] });
    showToast(`${LEVEL_LABELS[adding.type]} created`, 'success');
    setAdding(null);
  };

  const handleDelete = (id: string, type: 'zone' | 'section' | 'element' | 'subElement', e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDeleteId === id) {
      if (type === 'zone') deleteZone(id);
      else if (type === 'section') deleteSection(id);
      else if (type === 'element') deleteElement(id);
      else deleteSubElement(id);
      showToast(`${LEVEL_LABELS[type]} deleted`, 'info');
      setConfirmDeleteId(null);
    } else { setConfirmDeleteId(id); }
  };
  const cancelDelete = (e: React.MouseEvent) => { e.stopPropagation(); setConfirmDeleteId(null); };

  /* ========== Filter ========== */
  const matchesFilter = (subElement: SubElement): boolean => {
    if (statusFilter !== 'ALL' && subElement.status !== statusFilter) return false;
    return true;
  };

  const filteredZones = useMemo(() => {
    if (!activeProject) return [];
    if (statusFilter === 'ALL') return activeProject.zones;
    return activeProject.zones
      .map(zone => ({ ...zone, sections: zone.sections
        .map(section => ({ ...section, elements: section.elements
          .map(element => ({ ...element, subElements: element.subElements.filter(matchesFilter) }))
          .filter(el => el.subElements.length > 0) }))
        .filter(sec => sec.elements.length > 0) }))
      .filter(z => z.sections.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject, statusFilter]);

  const displayZones = useMemo(() => {
    const zones = [...filteredZones];
    zones.sort((a, b) => a.name.localeCompare(b.name));
    return zones;
  }, [filteredZones]);

  /* ========== Shared render helpers ========== */
  const numCols = 9;

  const renderCrudBtns = (id: string, code: string, name: string, type: 'zone' | 'section' | 'element' | 'subElement', childType?: AddingState['type'], childLabel?: string, onDuplicate?: (e: React.MouseEvent) => void) => {
    if (!isProjectManager) return null;
    if (confirmDeleteId === id) return null; // confirm/cancel renders in code cell
    return (
      <span className="ws-crud-actions" onClick={e => e.stopPropagation()}>
        {childType && <button className="ws-add-child-btn" onClick={e => startAdding(id, childType, e)} title={`Add ${childLabel}`}><PlusIcon /></button>}
        <button className="action-icon-btn edit" onClick={e => startEditing(id, code, name, e)} title="Edit"><EditIcon /></button>
        {onDuplicate && <button className="action-icon-btn duplicate" onClick={onDuplicate} title="Duplicate"><CopyIcon /></button>}
        <button className="action-icon-btn delete" onClick={e => handleDelete(id, type, e)} title="Delete"><DeleteIcon /></button>
      </span>
    );
  };

  const renderDeleteConfirm = (id: string, type: 'zone' | 'section' | 'element' | 'subElement') => {
    if (!isProjectManager || confirmDeleteId !== id) return null;
    return (
      <span className="ws-crud-actions ws-delete-confirm" onClick={e => e.stopPropagation()}>
        <button className="action-icon-btn save" onClick={e => handleDelete(id, type, e)} title="Confirm"><SaveIcon /></button>
        <button className="action-icon-btn cancel" onClick={cancelDelete} title="Cancel"><CancelIcon /></button>
      </span>
    );
  };

  const renderAddingRow = (indentClass: string) => {
    if (!adding) return null;
    return (
      <tr className="ws-row ws-adding-row" onClick={e => e.stopPropagation()}>
        <td className="ws-cell ws-cell-name">
          <div className={`ws-indent ${indentClass}`}>
            <input className="ws-inline-input" value={adding.name} onChange={e => setAdding({ ...adding, name: e.target.value })} placeholder="Name" onClick={e => e.stopPropagation()} />
          </div>
        </td>
        <td className="ws-cell ws-cell-code">
          <input className="ws-inline-input mono" value={adding.code} onChange={e => setAdding({ ...adding, code: e.target.value })} placeholder="CODE" onClick={e => e.stopPropagation()} autoFocus />
        </td>
        {isProjectManager && <td className="ws-cell ws-cell-actions"><span className="ws-crud-actions ws-crud-actions-visible"><button className="action-icon-btn save" onClick={saveAdding} title="Save"><SaveIcon /></button><button className="action-icon-btn cancel" onClick={cancelAdding} title="Cancel"><CancelIcon /></button></span></td>}
        <td className="ws-cell" colSpan={numCols - (isProjectManager ? 3 : 2)}></td>
      </tr>
    );
  };

  /* ========== No project ========== */
  if (!activeProject) {
    return (
      <div className="workspace-container">
        <div className="page-title-bar">
          <span className="page-title-icon"><WorkspaceIcon size={22} /></span>
          <div className="page-title-copy">
            <h1 className="workspace-title">Project Workspace</h1>
            <p>No active project.</p>
          </div>
        </div>
        <AppEmptyState
          icon={<WorkspaceIcon size={28} />}
          title="No active project"
          message="Select or create an active project to manage the workspace structure."
        />
      </div>
    );
  }

  const renderProgressBar = (pct: number) => (
    <div className="ws-progress-container">
      <div className="ws-progress-track">
        <div className="ws-progress-fill" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? 'var(--accent-green)' : pct >= 50 ? 'var(--accent-blue)' : 'var(--accent-amber)' }} />
      </div>
      <span className="ws-progress-text">{formatPercentage(pct)}</span>
    </div>
  );

  const getElementTasks = (element: Element): SubElement[] => element.subElements;
  const getSectionTasks = (section: Section): SubElement[] => section.elements.flatMap(getElementTasks);
  const getZoneTasks = (zone: Zone): SubElement[] => zone.sections.flatMap(getSectionTasks);
  const activeProjectsCount = projects.filter(project => project.status === 'ACTIVE').length;

  const getAggregateSummary = (tasks: SubElement[]) => {
    if (tasks.length === 0) return null;

    const uniqueAssignees = Array.from(new Set(tasks.flatMap(task => task.assignedTo)));
    const totalEstimatedHours = tasks.reduce((sum, task) => sum + task.estimatedHours, 0);
    const totalActualHours = tasks.reduce((sum, task) => sum + calculateActualHours(task.timeEntries), 0);
    const totalAttachments = tasks.reduce((sum, task) => sum + task.attachments.length, 0);
    const totalComments = tasks.reduce((sum, task) => sum + task.comments.length, 0);
    const startDates = tasks.map(task => task.startDate).filter(Boolean).sort();
    const endDates = tasks.map(task => task.endDate).filter(Boolean).sort();
    const highestPriorityTask = tasks.reduce((highest, task) => {
      const priorityOrder = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 } as const;
      if (!highest) return task;
      return priorityOrder[getEffectivePriority(task)] > priorityOrder[getEffectivePriority(highest)] ? task : highest;
    }, null as SubElement | null);

    return {
      assignees: uniqueAssignees,
      totalEstimatedHours,
      totalActualHours,
      totalAttachments,
      totalComments,
      startDate: startDates[0] || '',
      endDate: endDates[endDates.length - 1] || '',
      highestPriority: highestPriorityTask ? getEffectivePriority(highestPriorityTask) : null,
      isAutoEscalated: highestPriorityTask ? isAutoEscalated(highestPriorityTask) : false,
    };
  };

  const renderAggregateCells = (tasks: SubElement[]) => {
    const summary = getAggregateSummary(tasks);
    if (!summary) {
      return (
        <>
          <td className="ws-cell ws-cell-priority"></td>
          <td className="ws-cell ws-cell-assigned"></td>
          {FEATURES.TIME_TRACKING && <td className="ws-cell ws-cell-hours"></td>}
          <td className="ws-cell ws-cell-dates"></td>
          <td className="ws-cell ws-cell-attachments"></td>
        </>
      );
    }

    const totalAssignees = summary.assignees.length;
    const visibleAssignees = totalAssignees > 3 ? summary.assignees.slice(0, 2) : summary.assignees;
    const extraAssignees = totalAssignees > 3 ? totalAssignees - 2 : 0;

    return (
      <>
        <td className="ws-cell ws-cell-priority">
          {summary.highestPriority && (
            <>
              <span
                className="ws-priority-dot"
                style={{ backgroundColor: PriorityColors[summary.highestPriority] }}
              />
              <span className="ws-priority-text">
                {PriorityLabels[summary.highestPriority]}
                {summary.isAutoEscalated && <span className="priority-auto-badge">{'\u2191'}</span>}
              </span>
            </>
          )}
        </td>
        <td className="ws-cell ws-cell-assigned">
          <div className="ws-assignees">
            {visibleAssignees.map(uid => {
              const nm = getUserName(uid);
              return <Avatar key={uid} user={usersById.get(uid)} name={nm} size={28} className="ws-assignee-chip" title={nm} />;
            })}
            {extraAssignees > 0 && <span className="ws-assignee-chip ws-assignee-chip-more" title={`${extraAssignees} more assignees`}>+{extraAssignees}</span>}
          </div>
        </td>
        {FEATURES.TIME_TRACKING && (
          <td className="ws-cell ws-cell-hours">
            <span className="ws-hours-est">{formatHours(summary.totalEstimatedHours)}</span>
            <span className="ws-hours-sep">/</span>
            <span className={`ws-hours-act ${summary.totalActualHours > summary.totalEstimatedHours ? 'over' : ''}`}>{formatHours(summary.totalActualHours)}</span>
          </td>
        )}
        <td className="ws-cell ws-cell-dates">
          {summary.startDate && summary.endDate && <span className="ws-date-range">{formatDateShort(summary.startDate)} {'\u2014'} {formatDateShort(summary.endDate)}</span>}
        </td>
        <td className="ws-cell ws-cell-attachments">
          {summary.totalAttachments > 0 && <span className="ws-attachment-count"><PaperclipIcon size={14} />{summary.totalAttachments}</span>}
          {summary.totalComments > 0 && <span className="ws-comment-count"><ChatIcon size={14} />{summary.totalComments}</span>}
        </td>
      </>
    );
  };

  /* ==================== Row Renders ==================== */
  const renderSubElement = (se: SubElement) => {
    const actualHrs = calculateActualHours(se.timeEntries);
    const isEdit = editing?.id === se.id;
    return (
      <tr key={se.id} className="ws-row ws-row-subelement" onClick={() => !isEdit && navigate(`/workspace/${se.id}`)}>
        <td className="ws-cell ws-cell-name">
          <div className="ws-indent ws-indent-4">
            {isEdit ? (
              <input className="ws-inline-input" value={editing!.name} onChange={e => setEditing({ ...editing!, name: e.target.value })} onClick={e => e.stopPropagation()} />
            ) : (
              <span className="ws-name-text">{se.name}</span>
            )}
          </div>
        </td>
        <td className="ws-cell ws-cell-code">
          {isEdit ? (
            <input className="ws-inline-input mono" value={editing!.code} onChange={e => setEditing({ ...editing!, code: e.target.value })} onClick={e => e.stopPropagation()} />
          ) : (
            <span className="ws-code">{se.code}</span>
          )}
        </td>
        {isProjectManager && <td className="ws-cell ws-cell-actions">{isEdit ? (<span className="ws-crud-actions ws-crud-actions-visible" onClick={e => e.stopPropagation()}><button className="action-icon-btn save" onClick={e => saveEditing('subElement', e)}><SaveIcon /></button><button className="action-icon-btn cancel" onClick={cancelEditing}><CancelIcon /></button></span>) : <>{renderCrudBtns(se.id, se.code, se.name, 'subElement', undefined, undefined, (e) => { e.stopPropagation(); duplicateSubElement(se); })}{renderDeleteConfirm(se.id, 'subElement')}</>}</td>}
        <td className="ws-cell ws-cell-status">
          <span className="badge" style={{ backgroundColor: TaskStatusColors[se.status], color: '#fff' }}>{TaskStatusLabels[se.status]}</span>
        </td>
        <td className="ws-cell ws-cell-priority">
          <span
            className="ws-priority-dot"
            style={{ backgroundColor: PriorityColors[getEffectivePriority(se)] }}
          />
          <span
            className="ws-priority-text"
            title={isAutoEscalated(se) ? `Auto-escalated: ${se.endDate === new Date().toISOString().split('T')[0] ? 'deadline is today' : '1 day to deadline'}` : undefined}
          >
            {PriorityLabels[getEffectivePriority(se)]}
            {isAutoEscalated(se) && <span className="priority-auto-badge">{'\u2191'}</span>}
          </span>
        </td>
        <td className="ws-cell ws-cell-assigned">
          <div className="ws-assignees">
            {(() => {
              const total = se.assignedTo.length;
              const visible = total > 3 ? se.assignedTo.slice(0, 2) : se.assignedTo;
              const extra = total > 3 ? total - 2 : 0;
              return (
                <>
                  {visible.map(uid => {
                    const nm = getUserName(uid);
                    return <Avatar key={uid} user={usersById.get(uid)} name={nm} size={28} className="ws-assignee-chip" title={nm} />;
                  })}
                  {extra > 0 && (
                    <span className="ws-assignee-chip ws-assignee-chip-more" title={`+${extra} more`}>+{extra}</span>
                  )}
                </>
              );
            })()}
          </div>
        </td>
        {FEATURES.TIME_TRACKING && (
          <td className="ws-cell ws-cell-hours">
            <span className="ws-hours-est">{formatHours(se.estimatedHours)}</span>
            <span className="ws-hours-sep">/</span>
            <span className={`ws-hours-act ${actualHrs > se.estimatedHours ? 'over' : ''}`}>{formatHours(actualHrs)}</span>
          </td>
        )}
        <td className="ws-cell ws-cell-dates">
          {se.startDate && <span className="ws-date-range">{formatDateShort(se.startDate)} {'\u2014'} {formatDateShort(se.endDate)}</span>}
        </td>
        <td className="ws-cell ws-cell-attachments">
          {se.attachments.length > 0 && <span className="ws-attachment-count"><PaperclipIcon size={14} />{se.attachments.length}</span>}
          {se.comments.length > 0 && <span className="ws-comment-count"><ChatIcon size={14} />{se.comments.length}</span>}
        </td>
      </tr>
    );
  };

  const renderElement = (el: Element) => {
    const isExpanded = expandedElements.has(el.id);
    const progress = calculateElementProgress(el);
    const isEdit = editing?.id === el.id;
    const elementTasks = getElementTasks(el);
    return (
      <React.Fragment key={el.id}>
        <tr className={`ws-row ws-row-element ${highlightNodeId === el.id ? 'ws-row-focused' : ''}`} data-node-id={el.id} onClick={() => !isEdit && toggleElement(el.id)}>
          <td className="ws-cell ws-cell-name">
            <div className="ws-indent ws-indent-3">
              {isEdit ? (
                <input className="ws-inline-input" value={editing!.name} onChange={e => setEditing({ ...editing!, name: e.target.value })} onClick={e => e.stopPropagation()} />
              ) : (
                <>
                  <span className={`ws-chevron ${isExpanded ? 'open' : ''}`}>{'\u203A'}</span>
                  <span className="ws-name-text">{el.name}</span>
                  <span className="ws-child-count">({el.subElements.length})</span>
                </>
              )}
            </div>
          </td>
          <td className="ws-cell ws-cell-code">
            {isEdit ? (
              <input className="ws-inline-input mono" value={editing!.code} onChange={e => setEditing({ ...editing!, code: e.target.value })} onClick={e => e.stopPropagation()} />
            ) : (
              <span className="ws-code">{el.code}</span>
            )}
          </td>
          {isProjectManager && <td className="ws-cell ws-cell-actions">{isEdit ? (<span className="ws-crud-actions ws-crud-actions-visible" onClick={e => e.stopPropagation()}><button className="action-icon-btn save" onClick={e => saveEditing('element', e)}><SaveIcon /></button><button className="action-icon-btn cancel" onClick={cancelEditing}><CancelIcon /></button></span>) : <>{renderCrudBtns(el.id, el.code, el.name, 'element', 'subElement', 'Sub-Element', (e) => { e.stopPropagation(); duplicateElement(el); })}{renderDeleteConfirm(el.id, 'element')}</>}</td>}
          <td className="ws-cell ws-cell-status">{renderProgressBar(progress)}</td>
          {renderAggregateCells(elementTasks)}
        </tr>
        {isExpanded && el.subElements.map(renderSubElement)}
        {isExpanded && adding?.parentId === el.id && adding?.type === 'subElement' && renderAddingRow('ws-indent-4')}
      </React.Fragment>
    );
  };

  const renderSection = (sec: Section) => {
    const isExpanded = expandedSections.has(sec.id);
    const progress = calculateSectionProgress(sec);
    const isEdit = editing?.id === sec.id;
    const sectionTasks = getSectionTasks(sec);
    return (
      <React.Fragment key={sec.id}>
        <tr className={`ws-row ws-row-section ${highlightNodeId === sec.id ? 'ws-row-focused' : ''}`} data-node-id={sec.id} onClick={() => !isEdit && toggleSection(sec.id)}>
          <td className="ws-cell ws-cell-name">
            <div className="ws-indent ws-indent-2">
              {isEdit ? (
                <input className="ws-inline-input" value={editing!.name} onChange={e => setEditing({ ...editing!, name: e.target.value })} onClick={e => e.stopPropagation()} />
              ) : (
                <>
                  <span className={`ws-chevron ${isExpanded ? 'open' : ''}`}>{'\u203A'}</span>
                  <span className="ws-name-text">{sec.name}</span>
                  <span className="ws-child-count">({sec.elements.length} elements)</span>
                </>
              )}
            </div>
          </td>
          <td className="ws-cell ws-cell-code">
            {isEdit ? (
              <input className="ws-inline-input mono" value={editing!.code} onChange={e => setEditing({ ...editing!, code: e.target.value })} onClick={e => e.stopPropagation()} />
            ) : (
              <span className="ws-code">{sec.code}</span>
            )}
          </td>
          {isProjectManager && <td className="ws-cell ws-cell-actions">{isEdit ? (<span className="ws-crud-actions ws-crud-actions-visible" onClick={e => e.stopPropagation()}><button className="action-icon-btn save" onClick={e => saveEditing('section', e)}><SaveIcon /></button><button className="action-icon-btn cancel" onClick={cancelEditing}><CancelIcon /></button></span>) : <>{renderCrudBtns(sec.id, sec.code, sec.name, 'section', 'element', 'Element', (e) => { e.stopPropagation(); duplicateSection(sec); })}{renderDeleteConfirm(sec.id, 'section')}</>}</td>}
          <td className="ws-cell ws-cell-status">{renderProgressBar(progress)}</td>
          {renderAggregateCells(sectionTasks)}
        </tr>
        {isExpanded && sec.elements.map(renderElement)}
        {isExpanded && adding?.parentId === sec.id && adding?.type === 'element' && renderAddingRow('ws-indent-3')}
      </React.Fragment>
    );
  };

  const renderZone = (zone: Zone, zoneIndex: number) => {
    const isExpanded = expandedZones.has(zone.id);
    const progress = calculateZoneProgress(zone);
    const isEdit = editing?.id === zone.id;
    const zoneTasks = getZoneTasks(zone);
    return (
      <React.Fragment key={zone.id}>
        {zoneIndex > 0 && <tr className="ws-zone-spacer"><td colSpan={numCols}></td></tr>}
        <tr className={`ws-row ws-row-zone ${highlightNodeId === zone.id ? 'ws-row-focused' : ''}`} data-node-id={zone.id} onClick={() => !isEdit && toggleZone(zone.id)}>
          <td className="ws-cell ws-cell-name">
            <div className="ws-indent ws-indent-1">
              {isEdit ? (
                <input className="ws-inline-input" value={editing!.name} onChange={e => setEditing({ ...editing!, name: e.target.value })} onClick={e => e.stopPropagation()} />
              ) : (
                <>
                  <span className={`ws-chevron ${isExpanded ? 'open' : ''}`}>{'\u203A'}</span>
                  <span className="ws-zone-icon">
                    <FolderIcon size={16} />
                  </span>
                  <span className="ws-zone-name">{zone.name}</span>
                  <span className="ws-child-count">({zone.sections.length} sections)</span>
                </>
              )}
            </div>
          </td>
          <td className="ws-cell ws-cell-code">
            {isEdit ? (
              <input className="ws-inline-input mono" value={editing!.code} onChange={e => setEditing({ ...editing!, code: e.target.value })} onClick={e => e.stopPropagation()} />
            ) : (
              <span className="ws-code">{zone.code}</span>
            )}
          </td>
          {isProjectManager && <td className="ws-cell ws-cell-actions">{isEdit ? (<span className="ws-crud-actions ws-crud-actions-visible" onClick={e => e.stopPropagation()}><button className="action-icon-btn save" onClick={e => saveEditing('zone', e)}><SaveIcon /></button><button className="action-icon-btn cancel" onClick={cancelEditing}><CancelIcon /></button></span>) : <>{renderCrudBtns(zone.id, zone.code, zone.name, 'zone', 'section', 'Section', (e) => { e.stopPropagation(); duplicateZone(zone); })}{renderDeleteConfirm(zone.id, 'zone')}</>}</td>}
          <td className="ws-cell ws-cell-status">{renderProgressBar(progress)}</td>
          {renderAggregateCells(zoneTasks)}
        </tr>
        {isExpanded && zone.sections.map(renderSection)}
        {isExpanded && adding?.parentId === zone.id && adding?.type === 'section' && renderAddingRow('ws-indent-2')}
      </React.Fragment>
    );
  };

  return (
    <div className="workspace-container">
      <AppSectionHero
        icon={<WorkspaceIcon size={28} />}
        title="Project Workspace"
        subtitle={<>{activeProject.name} {'\u2014'} <strong>{activeProject.code}</strong></>}
        aside={
          <>
            <AppStatCard
              icon={<UsersIcon size={20} />}
              label="Team"
              value={activeProjectMembers.length}
              caption="Members"
              tone="blue"
            />
            <AppStatCard
              icon={<FolderIcon size={20} />}
              label="Projects"
              value={activeProjectsCount}
              caption="Active"
              tone="mint"
            />
          </>
        }
      />

      <AppToolbar
        className="workspace-reference-toolbar"
        left={
          <>
            <select
              className="app-surface-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as TaskStatus | 'ALL')}
            >
              <option value="ALL">All Statuses</option>
              {(Object.keys(TaskStatusLabels) as TaskStatus[]).map(s => <option key={s} value={s}>{TaskStatusLabels[s]}</option>)}
            </select>
          </>
        }
        right={
          <>
            <button className="app-surface-btn" onClick={expandAll}>Expand All</button>
            <button className="app-surface-btn" onClick={collapseAll}>Collapse All</button>
            {isProjectManager && (
              <>
                <button className="app-surface-btn" onClick={quickTask.open} title="New task (c)">+ Quick Task</button>
                <button className="app-surface-btn-primary" onClick={e => startAdding(activeProject.id, 'zone', e)}>+ Add Zone</button>
              </>
            )}
          </>
        }
      />

      {/* Hierarchical Table */}
      <div className="ws-table-wrapper">
        <table className="ws-table">
          <thead>
            <tr className="ws-head-row">
              <th className="ws-th ws-th-name">Name</th>
              <th className="ws-th ws-th-code">Code</th>
              {isProjectManager && <th className="ws-th ws-th-actions">Actions</th>}
              <th className="ws-th ws-th-status">Status</th>
              <th className="ws-th ws-th-priority">Priority</th>
              <th className="ws-th ws-th-assigned">Assigned</th>
              {FEATURES.TIME_TRACKING && <th className="ws-th ws-th-hours">Est / Actual</th>}
              <th className="ws-th ws-th-dates">Dates</th>
              <th className="ws-th ws-th-attachments">Files</th>
            </tr>
          </thead>
          <tbody>
            {adding?.type === 'zone' && adding?.parentId === activeProject.id && renderAddingRow('ws-indent-1')}
            {displayZones.map((zone, i) => renderZone(zone, i))}
            {displayZones.length === 0 && (
              <tr>
                <td colSpan={numCols} className="ws-empty-cell">
                  <AppEmptyState
                    icon={<WorkspaceIcon size={28} />}
                    title={activeProject.zones.length === 0 ? 'No workspace structure' : 'No matching tasks'}
                    message={activeProject.zones.length === 0
                      ? 'Add a section to start building zones, elements, and tasks.'
                      : 'Change the status filter to show more workspace items.'}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {quickTask.modal}
    </div>
  );
};
